import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';

const TimeToRecoveryChart = ({ data }) => {
  const recoveryData = useMemo(() => {
    return data
      .filter(item => item.healthCheckHistory && item.healthCheckHistory.length > 1)
      .map(item => {
        const history = item.healthCheckHistory;
        
        // Find first degraded/unhealthy point
        let degradedIndex = -1;
        for (let i = 0; i < history.length; i++) {
          const score = history[i].main_health?.score || 0;
          if (score < 80) {
            degradedIndex = i;
            break;
          }
        }

        // Find recovery point (back to healthy)
        let recoveryIndex = -1;
        if (degradedIndex >= 0) {
          for (let i = degradedIndex + 1; i < history.length; i++) {
            const score = history[i].main_health?.score || 0;
            if (score >= 80) {
              recoveryIndex = i;
              break;
            }
          }
        }

        // Calculate recovery time
        let recoveryTime = 0;
        let status = 'healthy';
        
        if (degradedIndex >= 0) {
          if (recoveryIndex >= 0) {
            recoveryTime = recoveryIndex - degradedIndex;
            status = 'recovered';
          } else {
            recoveryTime = history.length - degradedIndex;
            status = 'degraded';
          }
        }

        return {
          changeNumber: item.changeNumber,
          description: item.description,
          recoveryTime,
          status,
          currentHealth: item.health,
          currentScore: item.healthScore,
          healthCheckCount: history.length
        };
      })
      .filter(item => item.recoveryTime > 0)
      .sort((a, b) => b.recoveryTime - a.recoveryTime)
      .slice(0, 15);
  }, [data]);

  const maxRecoveryTime = Math.max(...recoveryData.map(d => d.recoveryTime), 1);

  const getStatusColor = (status) => {
    if (status === 'recovered') return '#10b981';
    if (status === 'degraded') return '#eab308';
    return '#6b7280';
  };

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <Clock size={24} className="text-cyan-accent" />
        <h3 className="text-xl font-bold text-cyan-accent">Time to Recovery</h3>
        <span className="text-sm text-gray-500">(Top 15 by recovery time)</span>
      </div>

      {recoveryData.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Clock size={48} className="mx-auto mb-4 opacity-50" />
          <p>No recovery data available</p>
          <p className="text-sm mt-2">Services need health check history to show recovery times</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {recoveryData.map((item, idx) => {
              const barWidth = (item.recoveryTime / maxRecoveryTime) * 100;
              
              return (
                <div key={idx} className="bg-dark-border/20 rounded-lg p-3 border border-dark-border/60">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-sm font-bold text-white">{item.changeNumber}</span>
                      <span className="text-xs text-gray-500 truncate">
                        {item.description.substring(0, 40)}...
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span 
                        className="text-xs px-2 py-1 rounded font-semibold"
                        style={{
                          backgroundColor: `${getStatusColor(item.status)}20`,
                          color: getStatusColor(item.status),
                          border: `1px solid ${getStatusColor(item.status)}40`
                        }}
                      >
                        {item.status}
                      </span>
                      <span className="text-sm font-bold text-cyan-accent">
                        {item.recoveryTime} checks
                      </span>
                    </div>
                  </div>

                  {/* Bar */}
                  <div className="relative h-8 bg-dark-border/40 rounded overflow-hidden border border-dark-border/60">
                    <div
                      className="absolute left-0 top-0 bottom-0 transition-all"
                      style={{
                        width: `${barWidth}%`,
                        backgroundColor: getStatusColor(item.status),
                        boxShadow: `inset 0 0 20px ${getStatusColor(item.status)}40`
                      }}
                    />
                    
                    {/* Time label */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-semibold text-white drop-shadow-lg">
                        {item.recoveryTime} health checks
                      </span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                    <span>Current: {item.currentHealth} ({item.currentScore})</span>
                    <span>{item.healthCheckCount} total checks</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="mt-6 pt-4 border-t border-dark-border/50 grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-lime-accent">
                {recoveryData.filter(d => d.status === 'recovered').length}
              </div>
              <div className="text-xs text-gray-400 mt-1">Recovered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-accent">
                {recoveryData.filter(d => d.status === 'degraded').length}
              </div>
              <div className="text-xs text-gray-400 mt-1">Still Degraded</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-accent">
                {Math.round(recoveryData.reduce((sum, d) => sum + d.recoveryTime, 0) / recoveryData.length)}
              </div>
              <div className="text-xs text-gray-400 mt-1">Avg Recovery</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-accent">
                {maxRecoveryTime}
              </div>
              <div className="text-xs text-gray-400 mt-1">Max Time</div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex items-center justify-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-lime-accent"></div>
              <span className="text-gray-400">Recovered (back to healthy)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-yellow-accent"></div>
              <span className="text-gray-400">Still Degraded</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TimeToRecoveryChart;
